import { evaluateProject, type AssemblyPanel, type EvaluatedProject, type ProjectDocument, type Vec3 } from '@scs/domain-engine';
import { partWorldBox, worldDeltaToLocal, type WBox } from './snap';
import type { Command } from './store';

/**
 * "Update connected": after panels changed size, the panels that touched them (resting on a moved
 * face, or flush with it while touching the panel) can follow that face. Everything is computed in
 * world space (µm) from the document before the resize and the current one.
 */

export interface PanelRef {
  unitId: string;
  panelKey: string;
}

export interface ConnectedMove extends PanelRef {
  label: string;
  /** World-space move, µm. */
  delta: Vec3;
  /** Same move in the assembly's local space (what is applied to `panel.position`). */
  local: Vec3;
}

const TOL = 500; // 0.5 mm
/** Panel key of a part (repeated copies are `${key}-${n}`); same rule as the properties panel. */
const keyOfPart = (panels: AssemblyPanel[], localKey: string) => panels.find((p) => p.key === localKey)?.key ?? panels.find((p) => p.repeat && localKey.startsWith(`${p.key}-`))?.key;
const refKey = (r: PanelRef) => `${r.unitId}/${r.panelKey}`;
const sized = (p: AssemblyPanel) => `${p.length}|${p.width}|${p.thickness}`;
const dims = (p: AssemblyPanel) => [p.length, p.width, p.thickness].sort((a, b) => a - b).join('|');

/** Panels whose length/width/thickness differ between two documents (same orientation). */
export function resizedPanels(before: ProjectDocument, after: ProjectDocument): PanelRef[] {
  const out: PanelRef[] = [];
  for (const a of after.assemblies) {
    const b = before.assemblies.find((x) => x.id === a.id);
    if (!b) continue;
    for (const p of a.panels) {
      const q = b.panels.find((x) => x.key === p.key);
      // A flip (orientation change) is not a resize.
      if (q && q.orientation === p.orientation && sized(q) !== sized(p) && dims(q) !== dims(p)) out.push({ unitId: a.id, panelKey: p.key });
    }
  }
  return out;
}

/**
 * The resize the user can still follow up with "Update connected": the trailing run of undo-history
 * commands that resized panels, measured from the document before the first of them.
 */
export function pendingResize(past: Command[], doc: ProjectDocument): { base: ProjectDocument; changed: PanelRef[] } | null {
  let i = past.length;
  while (i > 0 && resizedPanels(past[i - 1]!.before, past[i - 1]!.after).length > 0) i--;
  if (i === past.length || past.at(-1)!.after !== doc) return null;
  const base = past[i]!.before;
  const changed = resizedPanels(base, doc);
  return changed.length ? { base, changed } : null;
}

/** World boxes of every assembly panel (one per repeated copy). */
function panelBoxes(doc: ProjectDocument, ev: EvaluatedProject): Map<string, { ref: PanelRef; label: string; boxes: WBox[] }> {
  const m = new Map<string, { ref: PanelRef; label: string; boxes: WBox[] }>();
  for (const asm of doc.assemblies) {
    if (asm.hidden) continue;
    const unit = ev.cabinets.find((c) => c.instance.id === asm.id);
    if (!unit) continue;
    for (const part of unit.result.parts) {
      const key = keyOfPart(asm.panels, part.localKey);
      if (!key) continue;
      const ref = { unitId: asm.id, panelKey: key };
      const e = m.get(refKey(ref)) ?? { ref, label: asm.panels.find((p) => p.key === key)!.label, boxes: [] };
      e.boxes.push(partWorldBox(unit, part));
      m.set(refKey(ref), e);
    }
  }
  return m;
}

/** Face contact on one axis with real overlap on the other two. */
function touching(a: WBox, b: WBox): boolean {
  for (let k = 0; k < 3; k++) {
    const face = Math.abs(a.max[k]! - b.min[k]!) <= TOL || Math.abs(a.min[k]! - b.max[k]!) <= TOL;
    if (!face) continue;
    let overlap = true;
    for (let j = 0; j < 3; j++) if (j !== k && Math.min(a.max[j]!, b.max[j]!) - Math.max(a.min[j]!, b.min[j]!) <= TOL) overlap = false;
    if (overlap) return true;
  }
  return false;
}

const near = (a: number, b: number) => Math.abs(a - b) <= TOL;

/**
 * Moves of the panels connected to the resized ones. A neighbour follows a moved face of a source
 * when it rests on that face or is flush with it; one that spans the source's full extent on that
 * axis (e.g. a back panel as tall as the sides) is left alone. Followers pass the move on to panels
 * resting on them in the direction of the move.
 */
export function connectedMoves(base: ProjectDocument, doc: ProjectDocument, changed: PanelRef[], evBase = evaluateProject(base), evNow = evaluateProject(doc)): ConnectedMove[] {
  const before = panelBoxes(base, evBase);
  const now = panelBoxes(doc, evNow);
  const locked = new Set([...doc.assemblies.filter((a) => a.locked).map((a) => a.id)]);
  const fixed = new Set(changed.map(refKey));
  const moves = new Map<string, Vec3>();

  // Sources: per face, how far it moved.
  type Source = { box: WBox; dMin: Vec3; dMax: Vec3; translated: boolean };
  const queue: Source[] = [];
  for (const r of changed) {
    const b = before.get(refKey(r));
    const n = now.get(refKey(r));
    if (!b || !n || b.boxes.length !== 1 || n.boxes.length !== 1) continue;
    const [bb, nb] = [b.boxes[0]!, n.boxes[0]!];
    queue.push({ box: bb, dMin: [0, 1, 2].map((k) => nb.min[k]! - bb.min[k]!) as Vec3, dMax: [0, 1, 2].map((k) => nb.max[k]! - bb.max[k]!) as Vec3, translated: false });
  }

  while (queue.length) {
    const s = queue.shift()!;
    for (const [key, cand] of before) {
      if (fixed.has(key) || moves.has(key) || locked.has(cand.ref.unitId)) continue;
      // Only panels that are still where they were (not edited since).
      const cur = now.get(key);
      if (!cur || JSON.stringify(cur.boxes) !== JSON.stringify(cand.boxes)) continue;
      const box = cand.boxes.find((q) => touching(s.box, q));
      if (!box) continue;
      const delta: Vec3 = [0, 0, 0];
      for (let k = 0; k < 3; k++) {
        const atMax = near(box.min[k]!, s.box.max[k]!) || near(box.max[k]!, s.box.max[k]!);
        const atMin = near(box.max[k]!, s.box.min[k]!) || near(box.min[k]!, s.box.min[k]!);
        const spans = near(box.min[k]!, s.box.min[k]!) && near(box.max[k]!, s.box.max[k]!);
        if (s.translated) {
          // A moved follower carries what rests on it in the direction of its move.
          const d = s.dMax[k]!;
          if (d > 0 && (near(box.min[k]!, s.box.max[k]!) || (near(box.max[k]!, s.box.max[k]!) && !near(box.max[k]!, s.box.min[k]!)))) delta[k] = d;
          if (d < 0 && (near(box.max[k]!, s.box.min[k]!) || (near(box.min[k]!, s.box.min[k]!) && !near(box.min[k]!, s.box.max[k]!)))) delta[k] = d;
          continue;
        }
        if (spans) continue;
        if (atMax && s.dMax[k] !== 0) delta[k] = s.dMax[k]!;
        else if (atMin && s.dMin[k] !== 0) delta[k] = s.dMin[k]!;
      }
      if (delta.every((v) => v === 0)) continue;
      moves.set(key, delta);
      for (const q of cand.boxes) queue.push({ box: q, dMin: delta, dMax: delta, translated: true });
    }
  }

  const out: ConnectedMove[] = [];
  for (const [key, delta] of moves) {
    const e = before.get(key)!;
    const unit = evNow.cabinets.find((c) => c.instance.id === e.ref.unitId)!;
    out.push({ ...e.ref, label: e.label, delta, local: worldDeltaToLocal(unit, delta) });
  }
  return out;
}

/** Applies the moves to a document draft (inside one store command). */
export function applyConnectedMoves(d: ProjectDocument, moves: ConnectedMove[]) {
  for (const m of moves) {
    const p = d.assemblies.find((a) => a.id === m.unitId)?.panels.find((x) => x.key === m.panelKey);
    if (p) p.position = [p.position[0] + m.local[0], p.position[1] + m.local[1], p.position[2] + m.local[2]];
  }
}
